import { spErrorTags } from "@eventr/common";
import type { StateObject, ViewSpec } from "@eventr/native/navigation";
import { NativeNavigation } from "@eventr/native/navigation";
import { initReact } from "@eventr/native/navigation/react";
import type { NativeNavigationNavigatorOptions } from "@eventr/native/navigation/react-router";
import { matchPath } from "react-router-dom";
import { resolveLegacyEditPath } from "routing/legacyEditPaths";
import { allRouteNodes } from "routing/nodes";
import { isSheetPresentation, pathnameOf, type RouteNode } from "routing/route";
import { isModalRoot, MODAL_ROOT_STATE_KEY } from "./modalRoot";
import { isIosNativeNavigation } from "./platform";
import { paneEntryTab } from "./split-entries";
import { useSplitStore } from "./split.store";
import { installCrossRealmTouchEventShim } from "./touchEventShim";

/**
 * The native navigation React layer is initialised on iOS only. On Android and web
 * the plugin is never initialised and `nativeNavigationReact` is undefined; the app
 * uses the standard BrowserRouter path.
 */
function initNativeNavigationReact() {
  // Install before any touch handling so cross-webview touch events are recognised.
  installCrossRealmTouchEventShim();
  return initReact({ plugin: NativeNavigation });
}

export const nativeNavigationReact = isIosNativeNavigation() ? initNativeNavigationReact() : undefined;

/**
 * Navigator options for <NativeNavigationRouter>. Routes whose registry `meta.present`
 * is a modal style present as a native modal rather than a stack push. The integration
 * dismisses the modal automatically when navigation leaves its path or pops past its
 * root, so the screens keep navigating with the same pushPath / goBack calls; on web
 * and Android (plain BrowserRouter) these entries are ignored and the route is an
 * ordinary page.
 *
 * The list is derived from the registry: every route declaring `present` other than
 * `push` becomes an entry, so adding a modal is a one-line `meta` change with no wiring
 * here. A `fullScreen` present is a modal too (a full-cover root over the current tab),
 * which is how non-tab authed flows are shown.
 */
function modalEntry(node: RouteNode) {
  const style = node.meta.present;
  // Modal routes declare a modal presentation style; "push" (or unset) is a stack
  // push, not a modal, so such a route has no business in `modals`. Narrowing here
  // drops "push" from RoutePresentation, leaving exactly the plugin's PresentationStyle.
  if (style === undefined || style === "push") {
    throw new Error(`modalEntry: route ${node.path} has no modal presentation (present: ${style ?? "unset"})`);
  }
  // Sheets only: fullScreen carries onboarding and the pre-auth shell, which are
  // deliberately not dismissable. Marked on the root ViewSpec, never the StackSpec,
  // whose state mixes into every component pushed inside the sheet.
  const isSheet = isSheetPresentation(style);
  return {
    // Includes `modalMatch` so pushes to a modal's own sub-paths (e.g. `/edit`
    // presenting a sheet that then navigates to `/edit/sections/:sectionId`)
    // are recognised as staying within this modal's stack rather than being
    // treated as a plain push that dismisses it.
    path: [node.path, ...(node.meta.modalMatch ?? [])],
    presentOptions: (path: string, state?: StateObject) => ({
      component: {
        type: "stack" as const,
        components: [
          {
            type: "view" as const,
            path,
            state: isSheet ? { ...state, [MODAL_ROOT_STATE_KEY]: true } : state,
          },
        ],
      },
      style,
    }),
  };
}

const modalRoutes = allRouteNodes.filter((node) => node.meta.present !== undefined && node.meta.present !== "push");

const modals = modalRoutes.map(modalEntry);

/**
 * Presents `path` as its registry sheet in a stack named `stackId`, whatever the splits say, for
 * an entry opened away from its tab: the router would put it in the tab's trailing column.
 * Returns false when the route has no sheet.
 */
export async function presentRouteSheet(path: string, stackId: string, state?: StateObject): Promise<boolean> {
  const node = modalRoutes.find((route) => matchPath(route.path, pathnameOf(path)));
  if (!node) return false;
  const { component, style } = modalEntry(node).presentOptions(path, state);
  await NativeNavigation.present({ component: { ...component, id: stackId }, style });
  return true;
}

// Built once, on first use, from the same entries: the router tells modals apart by identity.
let modalsBesideSplits: typeof modals | undefined;
const modalsBesideSplitsOnce = () =>
  (modalsBesideSplits ??= modals.filter((_, index) => !paneEntryTab(modalRoutes[index]!.path)));

/**
 * `hidesTabBar` and `hidesNavBar` must be present on the ViewSpec at push time (see the
 * RouteMeta doc comment), so they can't be applied the same way as title/subtitle, which
 * use a reactive `update()` after the view is already on screen (see
 * NativeNavigationViewSetup.tsx). This is read by the react-router integration's push()
 * call for every navigation.
 */
export function viewOptionsForPath(path: string): Partial<ViewSpec> | undefined {
  // A caller may still push an old edit-itinerary path shape; react-router's redirect only
  // applies once the pushed view mounts, after this resolves, so it's applied here too.
  let resolvedPath = pathnameOf(path);
  let routeNode = allRouteNodes.find((node) => matchPath(node.path, resolvedPath));
  if (!routeNode) {
    const canonical = resolveLegacyEditPath(resolvedPath);
    if (canonical) {
      resolvedPath = canonical;
      routeNode = allRouteNodes.find((node) => matchPath(node.path, resolvedPath));
    }
  }
  if (!routeNode) {
    // Not a registered route or a recognised legacy shape. Surfaced rather than falling
    // back to an anonymous id, so a broken/unrouted push is distinguishable from "not migrated yet".
    spErrorTags("Pushed path matches no registered route", ["NATIVE_NAVIGATION"], { path });
    return undefined;
  }

  const result: Partial<ViewSpec> = {};

  const id = idForPath(resolvedPath);
  if (id) result.id = id;

  const hidesTabBarNode = allRouteNodes.find((route) => route.meta.hidesTabBar && matchPath(route.path, resolvedPath));
  const hidesNavBarNode = allRouteNodes.find((route) => route.meta.hidesNavBar && matchPath(route.path, resolvedPath));
  if (hidesTabBarNode) result.hidesTabBar = true;
  if (hidesNavBarNode) result.hidesNavBar = true;

  return result;
}

/**
 * A readable id for a pushed view: `/edit/sections/5` -> `view-edit-sections-5`.
 *
 * Suffixed on collision: the native side rejects an id already in use.
 */
function idForPath(path: string): string | undefined {
  const segments = pathnameOf(path).split("/").filter(Boolean);
  if (segments.length === 0) return undefined;

  const base = `view-${segments.join("-")}`;
  const taken = new Set(Object.values(nativeNavigationReact?.views() ?? {}).map((view) => view.id));
  if (!taken.has(base)) return base;

  let suffix = 2;
  while (taken.has(`${base}-${suffix}`)) suffix++;
  return `${base}-${suffix}`;
}

/** Whether a sheet is on screen: only modal roots carry the flag, and it dies with them. */
export function isModalPresented(): boolean {
  return presentedModalRootId() !== undefined;
}

/** The view id of the sheet on screen, or undefined when none is. */
export function presentedModalRootId(): string | undefined {
  return Object.values(nativeNavigationReact?.views() ?? {}).find((view) => isModalRoot(view.props.state))?.id;
}

/**
 * Whether a view for `pathname` is still alive. A view leaves this list only once its
 * webview has been torn down, so a closing sheet counts until it has fully gone.
 */
export function isViewOpenAt(pathname: string): boolean {
  return Object.values(nativeNavigationReact?.views() ?? {}).some(
    (view) => view.props.path !== undefined && pathnameOf(view.props.path) === pathname,
  );
}

/** Subscribes to views being created, updated or removed. A no-op off iOS. */
export function subscribeNativeViews(listener: () => void): () => void {
  return nativeNavigationReact?.addViewsListener(() => listener()) ?? (() => {});
}

export const nativeNavigationNavigatorOptions: NativeNavigationNavigatorOptions = {
  // Read on every navigation. While the tabs are splits, a pane tab's entry, such as a chat room,
  // opens in the trailing column rather than as its sheet.
  get modals() {
    return useSplitStore.getState().tabsAreSplits ? modalsBesideSplitsOnce() : modals;
  },
  viewOptionsForPath,
};
